// TransformationTool.js
import { useState } from "react";

export default function TransformationTool() {
  // We'll define an initial shape: a triangle with points in (x,y) form.
  // The shape is in "math coordinates" near origin, e.g. points around [-5..5].
  const [shape] = useState([
    { x: -2, y: 2 },    // A
    { x: 2,  y: 2 },    // B
    { x: 0,  y: -2 },   // C
  ]);

  // The user picks which transformation to apply
  const [transformation, setTransformation] = useState("none");
  // For rotation/dilation, we might need an angle or scale factor
  const [angleDeg, setAngleDeg] = useState(45);     // rotation degrees
  const [scaleFactor, setScaleFactor] = useState(1.5); // dilation scale

  // We'll define a coordinate-plane mapping: say 300×300 px,
  // center at (150,150) => (x=0,y=0).
  const width = 300;
  const height = 300;
  const centerX = width / 2;
  const centerY = height / 2;
  const scale = 20; // how many pixels per "1 unit" in math coords

  // We'll define a function to apply the chosen transformation
  function transformPoint(p) {
    let { x, y } = p;

    // 1) "Translate" => e.g. shift by (+3, +2)
    if (transformation === "translate") {
      x = x + 3;
      y = y + 2;
    }
    // 2) "Reflect" => let's do across x-axis => y => -y
    else if (transformation === "reflectX") {
      y = -y;
    }
    // 3) "Rotate" => rotate by angleDeg about (0,0)
    else if (transformation === "rotate") {
      const radians = (angleDeg * Math.PI) / 180;
      const xPrime = x * Math.cos(radians) - y * Math.sin(radians);
      const yPrime = x * Math.sin(radians) + y * Math.cos(radians);
      x = xPrime;
      y = yPrime;
    }
    // 4) "Dilate" => scale factor from origin
    else if (transformation === "dilate") {
      x = x * scaleFactor;
      y = y * scaleFactor;
    }
    // "none" => no transform, do nothing

    return { x, y };
  }

  // We'll produce a new array: the transformed shape
  const transformedShape = shape.map(transformPoint);

  // We'll convert from math coords => pixel coords
  function toPixel(p) {
    return {
      x: centerX + p.x * scale,
      y: centerY - p.y * scale, // minus because y-axis is inverted in SVG
    };
  }

  // We'll build polygon strings for original vs transformed
  const originalPoints = shape.map(toPixel).map(pt => `${pt.x},${pt.y}`).join(" ");
  const newPoints = transformedShape.map(toPixel).map(pt => `${pt.x},${pt.y}`).join(" ");

  return (
    <div style={{ border: "1px solid #ccc", padding: 10, borderRadius: 6 }}>
      <h4>Transformation Tool</h4>
      <p>
        This shows a triangle in the coordinate plane. Pick a transformation to see 
        how the new shape compares to the original.
      </p>
      <div style={{ marginBottom: 6 }}>
        <label>Transformation: </label>
        <select
        className="inputBox"
          value={transformation}
          onChange={(e) => setTransformation(e.target.value)}
          style={{ marginRight: 10 }}
        >
          <option value="none">None</option>
          <option value="translate">Translate (+3, +2)</option>
          <option value="reflectX">Reflect across x-axis</option>
          <option value="rotate">Rotate</option>
          <option value="dilate">Dilate (scale)</option>
        </select>

        {/* If rotate selected, show angle slider */}
        {transformation === "rotate" && (
          <label style={{ marginLeft: 8 }}>
            Angle (°):
            <input
              type="number"
              value={angleDeg}
              onChange={(e) => setAngleDeg(parseFloat(e.target.value))}
              style={{ width: 50, marginLeft: 5 }}
            />
          </label>
        )}

        {/* If dilate selected, show scale factor */}
        {transformation === "dilate" && (
          <label style={{ marginLeft: 8 }}>
            Scale factor:
            <input
              type="number"
              step="0.1"
              value={scaleFactor}
              onChange={(e) => setScaleFactor(parseFloat(e.target.value))}
              style={{ width: 60, marginLeft: 5 }}
            />
          </label>
        )}
      </div>

      <svg width={width} height={height} style={{ background: "#f9f9f9" }}>
        {/* We'll draw x-axis and y-axis lines for reference */}
        <line
          x1={0}
          y1={centerY}
          x2={width}
          y2={centerY}
          stroke="#999"
          strokeWidth="1"
        />
        <line
          x1={centerX}
          y1={0}
          x2={centerX}
          y2={height}
          stroke="#999"
          strokeWidth="1"
        />

        {/* Original shape => fill lightblue, stroke blue */}
        <polygon
          points={originalPoints}
          fill="#bbdefb"
          stroke="#0d47a1"
          strokeWidth="2"
        />

        {/* Transformed shape => fill pink, stroke red */}
        <polygon
          points={newPoints}
          fill="#ffcdd2"
          stroke="#c62828"
          strokeWidth="2"
        />
      </svg>

      <p style={{ marginTop: 8 }}>
        The <span style={{ color: "#0d47a1" }}>original</span> shape is in blue, 
        and the <span style={{ color: "#c62828" }}>transformed</span> shape is in red.
      </p>
      <p style={{ fontSize: "1.2rem", color: "#555" }}>
        Translations shift all points, reflections flip them, rotations spin them 
        around (0,0), and dilations scale them outward or inward.
      </p>
    </div>
  );
}
